import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight,
  Sparkles,
  MessageCircle,
  Phone,
  Send,
  Code2,
  Smartphone,
  Search,
  Palette,
  ShoppingCart,
  Megaphone,
  Bot,
  Cloud,
  Database,
  Globe,
  Layers,
  PenTool,
  Cpu,
  BarChart3,
} from "lucide-react";

const SIDE_ICONS_LEFT = [
  { Icon: Code2, color: "text-cyan-300" },
  { Icon: Smartphone, color: "text-emerald-300" },
  { Icon: Palette, color: "text-pink-300" },
  { Icon: Cloud, color: "text-sky-300" },
  { Icon: Database, color: "text-lime-300" },
  { Icon: Layers, color: "text-teal-300" },
  { Icon: PenTool, color: "text-rose-300" },
];

const SIDE_ICONS_RIGHT = [
  { Icon: Search, color: "text-yellow-300" },
  { Icon: Megaphone, color: "text-orange-300" },
  { Icon: ShoppingCart, color: "text-violet-300" },
  { Icon: Bot, color: "text-fuchsia-300" },
  { Icon: Globe, color: "text-indigo-300" },
  { Icon: Cpu, color: "text-purple-300" },
  { Icon: BarChart3, color: "text-amber-300" },
];

function SideOrbit({ items, side }: { items: typeof SIDE_ICONS_LEFT; side: "left" | "right" }) {
  const isLeft = side === "left";
  const [scattered, setScattered] = useState(false);
  const SIZE = 200;       // full circle diameter — icons stay strictly inside
  const ICON = 34;
  const R = SIZE / 2 - ICON / 2 - 4; // orbit radius keeps icons inside the ring
  const ORBIT_EASE = [0.22, 1, 0.36, 1] as const; // smooth premium easing

  return (
    <div
      // Hidden until xl so the orbit never sits over the centered headline/buttons
      // at md/lg widths. Position is anchored to the container edges (2px gap)
      // and a fixed top offset, so it stays stable regardless of banner padding.
      className="pointer-events-none absolute hidden lg:block"
      style={{
        width: SIZE,
        height: SIZE,
        top: "var(--orbit-top, 120px)",
        right: "var(--orbit-right, 2px)",
      }}
    >
      <div
        className="pointer-events-auto absolute inset-0"
        onClick={() => {
          setScattered(true);
          window.setTimeout(() => setScattered(false), 1800);
        }}
      >
        {/* dashed circle boundary */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full border border-dashed border-white/25"
        />
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: scattered ? 0 : isLeft ? 360 : -360 }}
          transition={
            scattered
              ? { duration: 0.5, ease: ORBIT_EASE }
              : { duration: 36, ease: "linear", repeat: Infinity }
          }
        >
          {items.map((it, i) => {
            const angle = (i / items.length) * 2 * Math.PI;
            const cx = SIZE / 2;
            const cy = SIZE / 2;
            const x = cx + R * Math.cos(angle);
            const y = cy + R * Math.sin(angle);
            const Icon = it.Icon;
            const flyX = Math.cos(angle) * 160 * (isLeft ? -1 : 1);
            const flyY = Math.sin(angle) * 160;
            return (
              <motion.div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                style={{ left: x, top: y }}
                animate={
                  scattered
                    ? { x: flyX, y: flyY, opacity: 0, scale: 0.4, rotate: 360 }
                    : { x: 0, y: 0, opacity: 1, scale: 1, rotate: isLeft ? -360 : 360 }
                }
                transition={
                  scattered
                    ? { duration: 1.1, ease: ORBIT_EASE }
                    : { duration: 36, ease: "linear", repeat: Infinity }
                }
                whileHover={{ scale: 1.35, zIndex: 10, transition: { duration: 0.25, ease: ORBIT_EASE } }}
              >
                <div
                  className="flex items-center justify-center rounded-full bg-white/95 ring-1 ring-white/40 shadow-md backdrop-blur transition-transform duration-200"
                  style={{ width: ICON, height: ICON }}
                >
                  <Icon className={`h-4 w-4 ${it.color}`} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}


export function CTABanner() {
  return (
    <section className="pt-24 pb-0 relative z-10 -mb-24 sm:-mb-32">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div
          className="relative overflow-hidden rounded-3xl border border-border bg-gradient-brand p-8 sm:p-12 lg:pr-[240px] text-white shadow-glow"
          style={{ ["--orbit-top" as any]: "120px", ["--orbit-right" as any]: "2px" }}
        >
          <SideOrbit items={[...SIDE_ICONS_LEFT, ...SIDE_ICONS_RIGHT]} side="right" />
          <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
            <div className="absolute -top-10 -right-10 h-64 w-64 rounded-full bg-white/15 blur-3xl" />
            <div className="absolute -bottom-10 -left-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          </div>
          <div className="relative text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold">
              <Sparkles className="h-3.5 w-3.5" /> AI consultation · free 30-min strategy call
            </div>
            <h2 className="mt-5 text-2xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-tight whitespace-nowrap">
              Ready to build something <span className="italic">remarkable</span>?
            </h2>
            <p className="mt-4 text-white/85">
              Tell us where you want to go. We'll come back within 24 hours with a roadmap, timeline and quote.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#0f2c59] shadow-sm hover:bg-white/95 transition">
                Book a call <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="mailto:hello@coderlity.com" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold hover:bg-white/20 transition">
                hello@coderlity.com
              </a>
            </div>
          </div>

          <div className="relative mt-8 pt-6 border-t border-white/20 flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/70">Or chat instantly</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a
                href="https://wa.me/966559758082"
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
                className="inline-flex items-center gap-2 rounded-full bg-white/95 pl-1.5 pr-4 py-1.5 ring-1 ring-white/40 shadow-md text-[#0f172a] text-sm font-semibold transition hover:-translate-y-0.5 hover:scale-[1.03] hover:shadow-glow"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white"><Phone className="h-[18px] w-[18px]" strokeWidth={2.25} /></span>
                WhatsApp
              </a>
              <a
                href="https://t.me/"
                target="_blank"
                rel="noreferrer"
                aria-label="Telegram"
                className="inline-flex items-center gap-2 rounded-full bg-white/95 pl-1.5 pr-4 py-1.5 ring-1 ring-white/40 shadow-md text-[#0f172a] text-sm font-semibold transition hover:-translate-y-0.5 hover:scale-[1.03] hover:shadow-glow"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#229ED9] text-white"><Send className="h-[18px] w-[18px]" strokeWidth={2.25} /></span>
                Telegram
              </a>
              <a
                href="https://m.me/"
                target="_blank"
                rel="noreferrer"
                aria-label="Live chat"
                className="inline-flex items-center gap-2 rounded-full bg-white/95 pl-1.5 pr-4 py-1.5 ring-1 ring-white/40 shadow-md text-[#0f172a] text-sm font-semibold transition hover:-translate-y-0.5 hover:scale-[1.03] hover:shadow-glow"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0084FF] text-white"><MessageCircle className="h-[18px] w-[18px]" strokeWidth={2.25} /></span>
                Live Chat
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
